/**
 * Installment-plan (a.k.a. EMI / BNPL / pay-over-time) conversion detector.
 *
 * A conversion cluster is any purchase where the bank splits a single big
 * charge into smaller monthly instalments. We detect it structurally so it
 * works globally regardless of what the bank calls it:
 *
 *   • India / GCC : "EMI", "OFFUS EMI, PRIN/INT NB", "OFAS EMI", "EMI PMTS"
 *   • US          : Affirm, Klarna, Amex Plan It, Citi Flex Plan,
 *                   Chase Pay Over Time, PayPal Pay Later
 *   • UK / EU     : Clearpay, Afterpay, Klarna, DivideBuy, PayPal Credit
 *   • Australia   : ZipPay, Afterpay, Humm, generic "instalment"
 *   • Fallback    : any row containing "installment" / "instalment" /
 *                   "payment plan" / "monthly plan"
 *
 * The core heuristic is symmetry: an installment plan almost always shows
 * as a purchase followed within a few days by a matching credit (the
 * bank's internal ledger reversal that moves the charge into a loan
 * account). We detect the pair, then classify the surrounding schedule
 * rows (principal / interest / fee / tax) with a regional-marker table.
 */

// -----------------------------------------------------------------------------
// Regional marker patterns — extendable. Each list is scanned in order.
// -----------------------------------------------------------------------------

const PRINCIPAL_PATTERNS = [
  // India — HDFC / ICICI / SBI
  /\bprin\s*nb\b/i,
  /\bofas\s+principal\b/i,
  /\bofas\s+prin\b/i,
  /\bemi\s+pmts\b/i,
  // US
  /\baffirm\s+payment\b/i,
  /\bklarna\s+payment\b/i,
  /\bplan\s*it\s+payment\b/i,       // Amex Plan It
  /\bflex\s*plan\s+payment\b/i,     // Citi Flex Plan
  /\bpay\s+over\s+time\b/i,         // Chase Pay Over Time
  /\bpaypal\s+pay\s+later\b/i,
  // UK / EU / AU
  /\bclearpay\b/i,
  /\bafterpay\b/i,
  /\bdividebuy\b/i,
  /\bzippay\b/i,
  /\bhumm\b/i,
  // Generic multi-region
  /\binstal?lment\s+principal\b/i,
  /\bmonthly\s+plan\s+payment\b/i,
  /\bpayment\s+plan\s+charge\b/i,
];

const INTEREST_PATTERNS = [
  // India
  /\bint\s*nb\b/i,
  /\bofas\s+int\b/i,
  // Generic
  /\binstal?lment\s+interest\b/i,
  /\bfinance\s+charge\b/i,          // US common
  /\bplan\s+interest\b/i,
];

const FEE_PATTERNS = [
  /\bprocng\s*fee\b/i,              // HDFC "OFFUS EMI, PROCNG FEE"
  /\bprocessing\s+fee\b/i,
  /\bplan\s+fee\b/i,                // Amex Plan It fee
  /\binstal?lment\s+fee\b/i,
  /\bsetup\s+fee\b/i,
  /\bbnpl\s+fee\b/i,
];

const TAX_PATTERNS = [
  /\bigst\b/i,                      // India — Integrated GST
  /\bcgst\b/i,                      // India — Central GST
  /\bsgst\b/i,                      // India — State GST
  /\bgst\b/i,                       // Australia / NZ / India
  /\bvat\b/i,                       // UK / EU
  /\bsales\s+tax\b/i,               // US
];

const REVERSAL_PATTERNS = [
  // India / GCC
  /\baggregator\s+emi\b/i,          // HDFC "Aggregator EMI - Offus Credit"
  /\bofas\s+conversion\b/i,         // ICICI
  /\bemi\s+conversion\b/i,
  // US
  /\bplan\s*it\s+conversion\s+credit\b/i,   // Amex Plan It
  /\bflex\s+plan\s+credit\b/i,               // Citi Flex Plan
  /\bpay\s+over\s+time\s+credit\b/i,         // Chase Pay Over Time
  /\baffirm\s+funding\b/i,                    // Affirm loan credit
  // UK / EU / AU
  /\bklarna\s+funding\b/i,
  // Generic multi-region
  /\binstal?lment\s+plan\s+credit\b/i,
  /\bplan\s+conversion\s+credit\b/i,
  /\bpurchase\s+split\b/i,          // BNPL generic (Clearpay/Afterpay)
  /\bbnpl\s+credit\b/i,
];

// -----------------------------------------------------------------------------
// Defaults
// -----------------------------------------------------------------------------

const DEFAULT_WINDOW_DAYS = 7;
const DEFAULT_AMOUNT_TOLERANCE = 1.0;    // absolute currency units
const MIN_CONVERSION_AMOUNT = 100;       // ignore tiny same-amount pairs

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/**
 * Detect installment-plan conversion clusters in a set of preview rows.
 *
 * @param {Array} rows - preview rows with at least
 *   { description, amount, type, txn_date, account_id? }
 * @param {Object} [opts]
 * @param {number} [opts.windowDays=7]
 * @param {number} [opts.amountTolerance=1.0]
 * @param {number} [opts.minAmount=100]
 * @returns {Array<Cluster>}
 *   Cluster = { purchase, reversal, principal[], interest[], fee[], tax[] }
 *   Each row entry carries its original `__idx` for update targeting.
 */
export function detectInstallmentConversions(rows, opts = {}) {
  const windowDays = opts.windowDays ?? DEFAULT_WINDOW_DAYS;
  const tolerance = opts.amountTolerance ?? DEFAULT_AMOUNT_TOLERANCE;
  const minAmt = opts.minAmount ?? MIN_CONVERSION_AMOUNT;

  if (!Array.isArray(rows) || rows.length === 0) return [];

  // Attach original index so caller can target updates precisely.
  const indexed = rows.map((r, i) => ({ ...r, __idx: i }));

  // Step 1 — find purchase / reversal pairs.
  const usedIndices = new Set();
  const pairs = [];
  for (let i = 0; i < indexed.length; i++) {
    if (usedIndices.has(i)) continue;
    const a = indexed[i];
    const amtA = Number(a.amount);
    if (!Number.isFinite(amtA) || Math.abs(amtA) < minAmt) continue;

    for (let j = i + 1; j < indexed.length; j++) {
      if (usedIndices.has(j)) continue;
      const b = indexed[j];
      const amtB = Number(b.amount);
      if (!Number.isFinite(amtB)) continue;

      // Opposite signs
      if (Math.sign(amtA) === Math.sign(amtB)) continue;
      // Same absolute amount within tolerance
      if (Math.abs(Math.abs(amtA) - Math.abs(amtB)) > tolerance) continue;
      // Same account (only enforce if both sides have it)
      if (a.account_id && b.account_id && a.account_id !== b.account_id) continue;
      // Within window
      if (_daysBetween(a.txn_date, b.txn_date) > windowDays) continue;
      // Guard: at least one side must carry an explicit plan-related
      // marker so we don't false-positive on unrelated same-amount pairs
      // (e.g. salary ↔ rent, refund ↔ purchase, transfer in ↔ transfer out).
      // Markers include: a reversal-pattern hit on either side, OR any
      // installment/EMI keyword in either description.
      const looksLikePlan =
        _matchesAny(a.description, REVERSAL_PATTERNS) ||
        _matchesAny(b.description, REVERSAL_PATTERNS) ||
        _hasPlanKeyword(a.description) ||
        _hasPlanKeyword(b.description);
      if (!looksLikePlan) continue;

      const purchase = amtA < 0 ? a : b;
      const reversal = amtA < 0 ? b : a;
      pairs.push({ purchase, reversal });
      usedIndices.add(i);
      usedIndices.add(j);
      break;
    }
  }

  // Step 2 — for each pair, collect related schedule rows in the same batch.
  return pairs.map(({ purchase, reversal }) => {
    const cluster = { purchase, reversal, principal: [], interest: [], fee: [], tax: [] };
    for (const row of indexed) {
      if (row.__idx === purchase.__idx || row.__idx === reversal.__idx) continue;
      const kind = _classifyScheduleRow(row);
      if (kind && cluster[kind]) cluster[kind].push(row);
    }
    return cluster;
  });
}

/**
 * Compute the row-level updates to apply when the user accepts the preset.
 * Returns a Map keyed by original row index → partial patch object.
 * Does NOT mutate anything.
 */
export function computeClusterPreset(cluster) {
  const updates = new Map();
  const patch = (row, partial) => updates.set(row.__idx, {
    ...partial,
    category_source: 'edited',
  });

  // Reversal → Transfer (cancels the phantom "income")
  patch(cluster.reversal, { type: 'transfer', category: 'Transfers' });

  // Principal instalments → Transfer (paying off the loan)
  for (const r of cluster.principal) {
    patch(r, { type: 'transfer', category: 'Transfers' });
  }

  // Interest → keep as expense with Fees & Charges category
  for (const r of cluster.interest) {
    patch(r, { type: 'expense', category: 'Fees & Charges' });
  }

  // Fees → Fees & Charges
  for (const r of cluster.fee) {
    patch(r, { type: 'expense', category: 'Fees & Charges' });
  }

  // Taxes → Taxes
  for (const r of cluster.tax) {
    patch(r, { type: 'expense', category: 'Taxes' });
  }

  // Purchase is intentionally NOT patched — user's category choice stays.
  return updates;
}

/** Sum a cluster's schedule rows to a compact summary for the UI. */
export function summarizeCluster(cluster) {
  const sumAbs = (arr) =>
    arr.reduce((acc, r) => acc + Math.abs(Number(r.amount) || 0), 0);
  return {
    principalCount: cluster.principal.length,
    interestCount: cluster.interest.length,
    feeCount: cluster.fee.length,
    taxCount: cluster.tax.length,
    principalTotal: sumAbs(cluster.principal),
    interestTotal: sumAbs(cluster.interest),
    feeTotal: sumAbs(cluster.fee),
    taxTotal: sumAbs(cluster.tax),
    purchaseAmount: Math.abs(Number(cluster.purchase.amount) || 0),
    purchaseDesc: cluster.purchase.description || '',
    reversalDesc: cluster.reversal.description || '',
    purchaseDate: cluster.purchase.txn_date,
    reversalDate: cluster.reversal.txn_date,
  };
}

// -----------------------------------------------------------------------------
// Internals
// -----------------------------------------------------------------------------

function _classifyScheduleRow(row) {
  const d = row?.description || '';
  if (!d) return null;
  if (_matchesAny(d, PRINCIPAL_PATTERNS)) return 'principal';
  if (_matchesAny(d, INTEREST_PATTERNS)) return 'interest';
  if (_matchesAny(d, FEE_PATTERNS)) return 'fee';
  if (_matchesAny(d, TAX_PATTERNS)) return 'tax';
  return null;
}

function _matchesAny(str, patterns) {
  if (typeof str !== 'string' || !str) return false;
  return patterns.some((p) => p.test(str));
}

// Broad, cheap "is this description in any way plan/installment-related?"
// check. Used as an additional guard when pairing purchase/reversal rows
// so we don't false-positive on coincidental same-amount opposite-sign
// pairs (e.g. salary ↔ rent, refund ↔ purchase, internal transfers).
const _PLAN_KEYWORD_RE = /\b(emi|instal?lment|payment\s+plan|monthly\s+plan|affirm|klarna|clearpay|afterpay|dividebuy|zippay|humm|plan\s*it|flex\s+plan|pay\s+over\s+time|paypal\s+pay\s+later|bnpl|offus|ofas)\b/i;
function _hasPlanKeyword(str) {
  if (typeof str !== 'string' || !str) return false;
  return _PLAN_KEYWORD_RE.test(str);
}

function _daysBetween(a, b) {
  const A = a instanceof Date ? a : new Date(a);
  const B = b instanceof Date ? b : new Date(b);
  if (!Number.isFinite(A.getTime()) || !Number.isFinite(B.getTime())) return Infinity;
  return Math.abs((A - B) / 86400000);
}

// Exported for tests only.
export const __private__ = {
  PRINCIPAL_PATTERNS,
  INTEREST_PATTERNS,
  FEE_PATTERNS,
  TAX_PATTERNS,
  REVERSAL_PATTERNS,
  _classifyScheduleRow,
  _matchesAny,
  _daysBetween,
};
